import React, { useMemo, useState } from 'react';
import type { DiagramType } from '../../lib/diagram/registries';
import { listRelationshipCategories, listRelationshipSubtypes, getRelationshipMapping, RELATIONSHIP_EXCLUSIVE_FLAGS, findSubtypeForEdge, propsEqual } from '../../lib/diagram/registries';
import { useDiagramStore } from '../../stores/diagramStore';
import type { ICommand } from '../../core/commands/ICommand';
import { UpdateEdgeCommand } from '../../core/commands/edges/UpdateEdgeCommand';
import { usePerspectiveStore } from '../../stores/perspectiveStore';
import { cn } from '@/lib/utils';
import { Search } from 'lucide-react';
import { getRelationshipIcon } from './relationship-icons';
import { Input } from './input';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { useTranslation } from '@/hooks/useTranslation';
import {
  attrsWithRelationSemantics,
  relationSemanticsForSubtype,
} from '../../lib/graph/relationshipSemantics';

interface RelationshipSubtypeSwitcherProps {
  diagramType: DiagramType;
  selectedEdgeIds: string[];
  onExecute: (cmds: ICommand[]) => void;
  className?: string;
  variant?: 'grid' | 'list';
  onClose?: () => void;
}

const EXCLUSIVE_FLAGS = RELATIONSHIP_EXCLUSIVE_FLAGS;
const ORG_ALLOWED_SUBTYPE_IDS = new Set([
  'reports_to',
  'assistant_to',
  'dotted_line_reports',
  'matrix_reports',
  'team_member',
  'occupies',
]);
const HIDDEN_GENOGRAM_SUBTYPE_IDS = new Set(['divorce', 'cohabitation', 'close']);

export const RelationshipSubtypeSwitcher: React.FC<RelationshipSubtypeSwitcherProps> = (props) => {
  if (!props.selectedEdgeIds || props.selectedEdgeIds.length === 0) return null;
  return <RelationshipSubtypeSwitcherBody {...props} />;
};

const RelationshipSubtypeSwitcherBody: React.FC<RelationshipSubtypeSwitcherProps> = ({
  diagramType,
  selectedEdgeIds,
  onExecute,
  className,
  variant = 'grid',
  onClose,
}) => {
  const { t } = useTranslation('diagram');
  const edges = useDiagramStore(s => s.edges);
  const categories = listRelationshipCategories(diagramType);
  const showCaptions = usePerspectiveStore.getState().showRelationshipCaptions;
  const primaryEdge = edges.find(ed => ed.id === selectedEdgeIds[0]);

  const defaultLabelFor = (edgeType: string, nextAttrs: Record<string, unknown> = {}): string => {
    const sub = findSubtypeForEdge(diagramType, edgeType, nextAttrs);
    if (sub) {
      return getSubtypeDisplayLabel(sub.label);
    }
    return 'Connection';
  };

  const handleSwitch = (subtypeId: string, subtypeLabel?: string) => {
    const mapping = getRelationshipMapping(diagramType, subtypeId);
    if (!mapping) return;
    const { edgeType, defaultEdgeProps } = mapping;

    // Clear mutually-exclusive flags before applying the next subtype defaults
    // Minimal scoped fix: prevent multiple subtypes showing as active
    const cmds: ICommand[] = [];
    for (const id of selectedEdgeIds) {
      const e = edges.find(ed => ed.id === id);
      if (!e) continue;
      // Start with existing attrs; force-clear exclusives by explicitly setting to false
      // because our store merges attrs shallowly and does not delete keys
      const base = { ...(e as any).attrs } as any;
      const exclusives = EXCLUSIVE_FLAGS[edgeType] || [];
      const cleared: Record<string, unknown> = {};
      // First, clear all exclusive flags to false
      for (const k of exclusives) {
        cleared[k] = false;
      }
      // Also reset styling keys that can linger between subtypes
      // e.g., cohabitation sets dashed true; switching back to divorce should reset dashed to false
      const RESET_STYLE_DEFAULTS: Record<string, Record<string, unknown>> = {
        marriage: { dashed: false, dash: undefined, dashDot: false, dashDoubleDot: false, dashSingleDot: false, dotted: false, separatedInFact: false, solidDashedDouble: false, cohabSeparation: false, nonSentimentalCohabSep: false, nonSentimentalCohab: false, temporaryRelation: false, legalCohabLegalSep: false, casualRelSep: false, separationInFactSlash: false, legalSeparationSlash: false, nullity: false, inLoveHarmony: false },
        sibling: {
          dashed: false,
          dash: undefined,
          dotted: false,
          color: '#111827',
          doubleLine: false,
          tripleLine: false,
          midTicks: false,
          midCircle: false,
          wave: false,
        },
        child: { dashed: false, dash: undefined, dotted: false, color: '#000000' },
        line: { dashed: false, dash: undefined, dashDot: false, dashDoubleDot: false, dashSingleDot: false, dotted: false, color: '#000000', doubleLine: false, tripleLine: false, midTicks: false, midCircle: false, wave: false, loveAffair: false, cutoffRepaired: false, neverMet: false, bestFriends: false, emotionalConnection: false, inLoveHarmony: false, distrust: false, fusedHostile: false, distanceHostile: false, closeHostile: false, distantViolence: false, closeViolence: false, focusedNegatively: false, abusePattern: false, physicalAbuse: false, emotionalAbuse: false, sexualAbuse: false, neglectAbuse: false, manipulative: false, controlling: false, jealous: false, fanAdmirer: false, limerence: false, plainNormal: false },
      };
      const styleDefaults = RESET_STYLE_DEFAULTS[edgeType] || {};
      // Reset style defaults for keys not explicitly set in the new subtype
      // This ensures old flags/styles are cleared, but preserves new subtype's explicit settings
      for (const [k, v] of Object.entries(styleDefaults)) {
        // If the incoming subtype does not explicitly set this key, reset it to default
        if (!Object.prototype.hasOwnProperty.call((defaultEdgeProps ?? {}) as Record<string, unknown>, k)) {
          cleared[k] = v as any;
        }
      }
      // Merge: base (old attrs) -> cleared (all flags reset) -> defaultEdgeProps (new subtype defaults)
      // This ensures old flags are cleared before new ones are applied
      const mergedAttrs = { ...base, ...cleared, ...(defaultEdgeProps as any) } as any;
      const shouldAutoLabel = showCaptions && (
        mergedAttrs.label == null || (mergedAttrs as any).__autoLabel === true || mergedAttrs.label === 'Connection'
      );
      const shouldForceSubtypeLabel = diagramType === 'genogram' && subtypeId === 'divorce_v2';
      if (shouldAutoLabel || shouldForceSubtypeLabel) {
        mergedAttrs.label = subtypeLabel || defaultLabelFor(edgeType, mergedAttrs);
        (mergedAttrs as any).__autoLabel = true;
      }
      const semantics = relationSemanticsForSubtype({
        diagramType,
        subtypeId,
        qualifierSource: mergedAttrs,
        originalLabel: subtypeId,
      });
      cmds.push(new UpdateEdgeCommand(e.id, {
        type: edgeType as any,
        attrs: attrsWithRelationSemantics(mergedAttrs, semantics, subtypeId),
      }));
    }
    if (cmds.length > 0) {
      if (diagramType === 'org' && subtypeId === 'matrix_reports') {
        console.info('[MatrixScenarioDebug] subtype switch requested', {
          selectedEdgeIds,
          commandCount: cmds.length,
          commands: cmds.map((command) => ({ id: command.id, name: command.name })),
        });
      }
      onExecute(cmds);
      onClose?.();
    }
  };

  const isActiveSubtype = (subtypeId: string): boolean => {
    if (!primaryEdge) return false;
    const mapping = getRelationshipMapping(diagramType, subtypeId);
    if (!mapping) return false;
    if ((primaryEdge as any).type !== (mapping.edgeType as any)) return false;
    const expected = (mapping.defaultEdgeProps || {}) as Record<string, unknown>;
    // Check all expected props match (deep compare for objects/arrays like startArrow, dash)
    for (const [k, v] of Object.entries(expected)) {
      if (!propsEqual((primaryEdge as any).attrs?.[k], v)) return false;
    }
    // For exclusive flags: if the subtype does NOT expect a flag, 
    // the edge must NOT have that flag set (to prevent multiple matches)
    const exclusives = EXCLUSIVE_FLAGS[mapping.edgeType || ''] || [];
    for (const f of exclusives) {
      const subtypeExpectsFlag = Object.prototype.hasOwnProperty.call(expected, f);
      const edgeHasFlag = Boolean((primaryEdge as any).attrs?.[f]);
      // If subtype doesn't expect this flag but edge has it, not a match
      if (!subtypeExpectsFlag && edgeHasFlag) {
        return false;
      }
    }
    return true;
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const isOrgDiagram = diagramType === 'org';

  const normalizeSearchTerm = (term: string): string => {
    return term.trim().toLowerCase();
  };

  const getSubtypeDisplayLabel = (label: string): string => {
    if (label.startsWith('relationships.subtypes.')) {
      return t(label, label);
    }
    if (label.startsWith('connector.types.')) {
      return t(label, label);
    }
    return label;
  };

  const allSubtypes = useMemo(() => {
    const all: Array<{ subtype: ReturnType<typeof listRelationshipSubtypes>[0]; categoryId: string }> = [];
    categories.forEach(cat => {
      const subs = listRelationshipSubtypes(diagramType, cat.id);
      subs.forEach(sub => {
        if (diagramType === 'genogram' && HIDDEN_GENOGRAM_SUBTYPE_IDS.has(sub.id)) return;
        all.push({ subtype: sub, categoryId: cat.id });
      });
    });
    return all;
  }, [diagramType, categories]);

  const orgSubtypes = useMemo(() => {
    return allSubtypes
      .map(item => item.subtype)
      .filter(subtype => ORG_ALLOWED_SUBTYPE_IDS.has(subtype.id));
  }, [allSubtypes]);

  const orgFilteredSubtypes = useMemo(() => {
    const normalizedSearch = normalizeSearchTerm(searchTerm);
    if (!normalizedSearch) return orgSubtypes;
    return orgSubtypes.filter(item => {
      const label = getSubtypeDisplayLabel(item.label).toLowerCase();
      const synonyms = (item.synonyms || []).map(s => s.toLowerCase());
      return label.includes(normalizedSearch) || synonyms.some(s => s.includes(normalizedSearch));
    });
  }, [orgSubtypes, searchTerm]);

  const filteredSubtypes = useMemo(() => {
    const normalizedSearch = normalizeSearchTerm(searchTerm);
    let filtered = allSubtypes;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.categoryId === selectedCategory);
    }

    if (normalizedSearch) {
      filtered = filtered.filter(item => {
        const label = getSubtypeDisplayLabel(item.subtype.label).toLowerCase();
        const synonyms = (item.subtype.synonyms || []).map(s => s.toLowerCase());
        return label.includes(normalizedSearch) || synonyms.some(s => s.includes(normalizedSearch));
      });
    }

    return filtered;
  }, [allSubtypes, selectedCategory, searchTerm]);

  const getCategoryDisplayLabel = (categoryId: string): string => {
    return t(`relationships.categories.${categoryId}`, categoryId);
  };

  const selectedCategoryObj = useMemo(() => {
    return selectedCategoryId ? categories.find(c => c.id === selectedCategoryId) || null : null;
  }, [categories, selectedCategoryId]);

  const subtypesForSelectedCategory = useMemo(() => {
    if (!selectedCategoryId) return [];
    return listRelationshipSubtypes(diagramType, selectedCategoryId).filter(
      (sub) => diagramType !== 'genogram' || !HIDDEN_GENOGRAM_SUBTYPE_IDS.has(sub.id),
    );
  }, [diagramType, selectedCategoryId]);

  const searchResults = useMemo(() => {
    if (!searchTerm) return [];
    const normalizedSearch = normalizeSearchTerm(searchTerm);
    let results = allSubtypes;
    if (selectedCategoryId) {
      results = results.filter(item => item.categoryId === selectedCategoryId);
    }
    return results.filter(item => {
      const label = item.subtype.label.toLowerCase();
      const synonyms = (item.subtype.synonyms || []).map(s => s.toLowerCase());
      return label.includes(normalizedSearch) || synonyms.some(s => s.includes(normalizedSearch));
    });
  }, [allSubtypes, searchTerm, selectedCategoryId]);

  const groupedSubtypes = useMemo(() => {
    const grouped: Record<string, Array<{ subtype: ReturnType<typeof listRelationshipSubtypes>[0]; categoryId: string }>> = {};
    filteredSubtypes.forEach(item => {
      if (!grouped[item.categoryId]) {
        grouped[item.categoryId] = [];
      }
      grouped[item.categoryId].push(item);
    });
    return grouped;
  }, [filteredSubtypes]);

  const visibleCategories = useMemo(() => {
    if (selectedCategory === 'all') {
      return categories.filter(cat => groupedSubtypes[cat.id]?.length > 0);
    }
    return categories.filter(cat => cat.id === selectedCategory && groupedSubtypes[cat.id]?.length > 0);
  }, [categories, selectedCategory, groupedSubtypes]);

  if (variant === 'list') {
    const searchPlaceholder = diagramType === 'org'
      ? t('orgchart.connectorSearchPlaceholder', 'Search (e.g. reports to)')
      : t('relationships.searchPlaceholder', 'Search Relationships');
    if (isOrgDiagram) {
      return (
        <Command className={cn('w-[280px] max-w-full', className)}>
          <CommandInput
            data-testid="tid.ui.quickCreate.search"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onValueChange={setSearchTerm}
            className="h-8 text-xs"
          />
          <CommandList className="max-h-[360px]">
            <CommandEmpty>{t('relationships.noRelationshipsFound', 'No relationships found')}</CommandEmpty>
            <CommandGroup>
              {orgFilteredSubtypes.map(s => {
                const isActive = isActiveSubtype(s.id);
                return (
                  <CommandItem
                    key={s.id}
                    value={s.id}
                    onSelect={() => handleSwitch(s.id, getSubtypeDisplayLabel(s.label))}
                    className={cn(
                      'text-xs cursor-pointer',
                      isActive ? 'bg-accent text-accent-foreground' : ''
                    )}
                    data-testid={`tid.ui.relationship.subtype.${s.id}`}
                  >
                    {getSubtypeDisplayLabel(s.label)}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      );
    }
    return (
      <Command className={cn('w-[280px] max-w-full', className)}>
        <CommandInput
          data-testid="tid.ui.quickCreate.search"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onValueChange={setSearchTerm}
          className="h-8 text-xs"
        />
        <CommandList className="max-h-[360px]">
          <CommandEmpty>{t('relationships.noRelationshipsFound', 'No relationships found')}</CommandEmpty>

          {/* Breadcrumb chips */}
          {selectedCategoryObj && (
            <div className="px-2 pt-1">
              <div className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-2 py-1">
                <button
                  className="text-[12px] font-medium text-foreground/80 hover:underline"
                  onClick={() => {
                    setSelectedCategoryId(null);
                    setSearchTerm('');
                  }}
                >
                  {getCategoryDisplayLabel(selectedCategoryObj.id)}
                </button>
              </div>
            </div>
          )}

          {/* Search results */}
          {searchTerm && !selectedCategoryId && (
            <CommandGroup heading={t('quickCreatePanel.resultsHeading', { defaultValue: 'Results' })}>
              {searchResults.map(({ subtype: s }) => {
                const isActive = isActiveSubtype(s.id);
                return (
                  <CommandItem
                    key={s.id}
                    value={s.id}
                    onSelect={() => {
                      setSelectedCategoryId(s.categoryId);
                      handleSwitch(s.id, getSubtypeDisplayLabel(s.label));
                      setSearchTerm('');
                    }}
                    className={cn(
                      'text-xs cursor-pointer',
                      isActive ? 'bg-accent text-accent-foreground' : ''
                    )}
                    data-testid={`tid.ui.relationship.subtype.${s.id}`}
                  >
                    {getSubtypeDisplayLabel(s.label)}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          {/* Categories (when no category selected and no search) */}
          {!searchTerm && !selectedCategoryId && (
            <CommandGroup heading={t('quickCreatePanel.categoriesHeading', { defaultValue: 'Categories' })}>
              {categories.map(cat => (
                <CommandItem
                  key={cat.id}
                  value={cat.id}
                  onSelect={() => {
                    setSelectedCategoryId(cat.id);
                    setSearchTerm('');
                  }}
                  className="text-xs cursor-pointer"
                  data-testid={`tid.ui.relationship.category.${cat.id}`}
                >
                  {getCategoryDisplayLabel(cat.id)}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Subtypes (when category selected, no search) */}
          {!searchTerm && selectedCategoryId && (
            <CommandGroup heading={t('quickCreatePanel.subtypesHeading', { defaultValue: 'Subtypes' })}>
              {subtypesForSelectedCategory.map(s => {
                const isActive = isActiveSubtype(s.id);
                return (
                  <CommandItem
                    key={s.id}
                    value={s.id}
                    onSelect={() => handleSwitch(s.id, getSubtypeDisplayLabel(s.label))}
                    className={cn(
                      'text-xs cursor-pointer',
                      isActive ? 'bg-accent text-accent-foreground' : ''
                    )}
                    data-testid={`tid.ui.relationship.subtype.${s.id}`}
                  >
                    {getSubtypeDisplayLabel(s.label)}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          {/* Search results when category is selected */}
          {searchTerm && selectedCategoryId && (
            <CommandGroup heading={t('quickCreatePanel.resultsHeading', { defaultValue: 'Results' })}>
              {searchResults.map(({ subtype: s }) => {
                const isActive = isActiveSubtype(s.id);
                return (
                  <CommandItem
                    key={s.id}
                    value={s.id}
                    onSelect={() => {
                      handleSwitch(s.id, getSubtypeDisplayLabel(s.label));
                      setSearchTerm('');
                    }}
                    className={cn(
                      'text-xs cursor-pointer',
                      isActive ? 'bg-accent text-accent-foreground' : ''
                    )}
                    data-testid={`tid.ui.relationship.subtype.${s.id}`}
                  >
                    {getSubtypeDisplayLabel(s.label)}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    );
  }

  return (
    <div className={cn("flex flex-col h-full min-w-0", className)}>
      <div className="p-4 border-b space-y-3 flex-shrink-0 min-w-0">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t('relationships.searchPlaceholder', 'Search Relationships')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="tid.ui.relationship.search"
            className="pl-8 h-8 text-sm w-full max-w-[398px]"
          />
        </div>
        {!isOrgDiagram && (
          <div className="relative -mx-4 px-4 min-w-0 w-full overflow-x-auto overflow-y-hidden">
            <div className="flex gap-0 pb-1 pr-4 inline-flex" style={{ width: 'max-content', minWidth: '100%' }}>
              <button
                type="button"
                onClick={() => setSelectedCategory('all')}
                data-testid="tid.ui.relationship.category.all"
                className={cn(
                  "px-3 py-2 text-xs font-medium text-foreground transition-colors border-b-2 -mb-[1px] whitespace-nowrap flex-shrink-0",
                  selectedCategory === 'all'
                    ? "border-foreground"
                    : "border-transparent hover:border-foreground/30"
                )}
              >
                {t('relationships.all', 'All')}
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(cat.id)}
                  data-testid={`tid.ui.relationship.category.${cat.id}`}
                  className={cn(
                    "px-3 py-2 text-xs font-medium text-foreground transition-colors border-b-2 -mb-[1px] whitespace-nowrap flex-shrink-0",
                    selectedCategory === cat.id
                      ? "border-foreground"
                      : "border-transparent hover:border-foreground/30"
                  )}
                >
                  {getCategoryDisplayLabel(cat.id)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-4">
          {isOrgDiagram && orgFilteredSubtypes.length === 0 ? (
            <div className="text-center text-muted-foreground py-8 text-sm">
              {t('relationships.noRelationshipsFound', 'No relationships found')}
            </div>
          ) : isOrgDiagram ? (
            <div className="grid gap-1.5 grid-cols-4 w-full max-w-[398px]">
              {orgFilteredSubtypes.map(s => {
                const iconPath = getRelationshipIcon(s.id, diagramType);
                const isActive = isActiveSubtype(s.id);
                return (
                  <button
                    key={s.id}
                    onClick={() => handleSwitch(s.id, getSubtypeDisplayLabel(s.label))}
                    data-testid={`tid.ui.relationship.subtype.${s.id}`}
                    className={cn(
                      'flex flex-col items-center justify-center gap-1.5 p-2.5 rounded-lg border transition-all',
                      'bg-background hover:bg-muted/50',
                      'h-[96px] w-full min-w-0',
                      isActive
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                        : 'border-border hover:border-foreground/20'
                    )}
                    title={getSubtypeDisplayLabel(s.label)}
                  >
                    {iconPath && (
                      <img
                        src={iconPath}
                        alt={getSubtypeDisplayLabel(s.label)}
                        className="h-5 w-auto flex-shrink-0"
                      />
                    )}
                    <span className="text-xs text-center text-muted-foreground leading-tight line-clamp-2">
                      {getSubtypeDisplayLabel(s.label)}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : visibleCategories.length === 0 ? (
            <div className="text-center text-muted-foreground py-8 text-sm">
              {t('relationships.noRelationshipsFound', 'No relationships found')}
            </div>
          ) : (
            <div className="space-y-4">
              {visibleCategories.map(cat => {
                const subs = groupedSubtypes[cat.id] || [];
                if (subs.length === 0) return null;

                return (
                  <div key={cat.id}>
                    <h3 className="text-xs font-semibold text-foreground mb-3">
                      {getCategoryDisplayLabel(cat.id)}
                    </h3>
                    <div className="grid gap-1.5 grid-cols-5 w-full max-w-[398px]">
                      {subs.map(({ subtype: s }) => {
                        const iconPath = getRelationshipIcon(s.id, diagramType);
                        const isActive = isActiveSubtype(s.id);
                        return (
                          <button
                            key={s.id}
                            onClick={() => handleSwitch(s.id, getSubtypeDisplayLabel(s.label))}
                            data-testid={`tid.ui.relationship.subtype.${s.id}`}
                            className={cn(
                              'flex flex-col items-center justify-center gap-1.5 p-2.5 rounded-lg border transition-all',
                              'bg-background hover:bg-muted/50',
                              'h-[96px] w-full min-w-0',
                              isActive
                                ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                                : 'border-border hover:border-foreground/20'
                            )}
                            title={getSubtypeDisplayLabel(s.label)}
                          >
                            {iconPath && (
                              <img
                                src={iconPath}
                                alt={getSubtypeDisplayLabel(s.label)}
                                className="h-5 w-auto flex-shrink-0"
                              />
                            )}
                            <span className="text-xs text-center text-muted-foreground leading-tight line-clamp-2">
                              {getSubtypeDisplayLabel(s.label)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
